/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentUser, getCurrentUserProperties } from './slices/user';
import useCurrentUser from 'shared/../hooks/useCurrentUser';
import {
  BrowserRouter as Router, Route, Switch, useLocation, useHistory,
} from 'react-router-dom';
import * as localForage from 'localforage';
import authFetch from 'src/fetch/fetch';
import { OrderSupportRequestsConnectionProvider } from 'shared/layout/RequestsPopup';
// MSAL
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import RequestInterceptor from './RequestInterceptor';
import useTokenGetter from './shared/hooks/useTokenGetter';
// components
import toastr from 'toastr';
import AppLayout from 'shared/layout/AppLayout';
import AccessRoute from 'shared/router/AccessRoute';
import LoaderSpinner from 'shared/indicators/LoaderSpinner';
import { ToastContainer } from './shared/components/toast';
import ModalContainer from 'src/shared/components/modals/ModalContainer';
import { ConfirmDialogRoot } from './shared/components/modals/ConfirmDialogue';
import ReactTooltip from 'react-tooltip';
import { UAParser } from 'ua-parser-js';
// Errors catching
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './ErrorFallback';
// Messaing
import { Messaging as EmployeeMessaging, UIContextProvider } from '@gulfwinds/ui-components';
import { setMessagingUrl } from './slices/messagingUrl';
// constants
import { SHOULD_LOADS_REDIRECT } from 'shared/../constants/Globals';
import { VIEW_LOADS_ON_LOGIN, CAN_VIEW_CMS } from 'shared/../constants/UserAccess';
// fetch
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getMessagingUrl as fetchMessagingUrl, getMessagingUrl2, setMessagingV2Client } from 'src/fetch/messaging';
import { trackDevice } from 'src/fetch/users';
import { getSignalRToken } from './fetch/signalR';
// Theme
import { theme, switchTheme } from './theme';
// Hooks
import { useDataGridSettingsCache } from './shared/hooks/useDataGridSettingsCache';
import { useAppMsalAuthentication } from './shared/hooks/useAppMsalAuthentication';

// Styles (import shared and components styles for dark mode)
if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './shared/css/index.scss?dark');
  import(/* webpackChunkName: "dark" */ './shared/css/custom/07-components/_index.scss?dark');
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // this tells react-query to refetch on a react component update if it happens after that time
      staleTime: 1000 * 2, // 2 seconds
      // this clears the cache after a certain time. So a newly mounted hook will refetch after time, but component updates are coverd above
      cacheTime: 1000 * 2, // 2 seconds
    },
  },
});

// Pages Lazy Loading
const onError = (error) => {
  if (error?.name === 'ChunkLoadError') {
    window.location.reload();
  }
  throw error;
};

const AccessDenied = lazy(() => import(/* webpackChunkName: "access-denied" */ 'shared/pages/AccessDenied')
  .catch(onError));
const Admin = lazy(() => import(/* webpackChunkName: "admin" */ './admin/components').catch(onError));
const Audits = lazy(() => import(/* webpackChunkName: "audits" */ './audits/components').catch(onError));
const Dispatching = lazy(() => import(/* webpackChunkName: "dispatching" */ './dispatching/components').catch(onError));
const Invoicing = lazy(() => import(/* webpackChunkName: "invoicing" */ './invoices/components').catch(onError));
const Landing = lazy(() => import(/* webpackChunkName: "landing" */ './landing/components').catch(onError));
const Maintenance = lazy(() => import(/* webpackChunkName: "maintenance" */ './maintenance/components').catch(onError));
const OrganizationsComponent = lazy(() => import(/* webpackChunkName: "organizations" */ './organizations/components')
  .catch(onError));
const Pricing = lazy(() => import(/* webpackChunkName: "pricing" */ './pricing/components').catch(onError));
const Risk = lazy(() => import(/* webpackChunkName: "risk" */ './risk/components').catch(onError));
const Safety = lazy(() => import(/* webpackChunkName: "safety" */ './safety/components').catch(onError));
const Scheduling = lazy(() => import(/* webpackChunkName: "scheduling" */ './scheduling/components').catch(onError));
const Warehouse = lazy(() => import(/* webpackChunkName: "warehouse" */ './warehouse/components').catch(onError));

const routes = [
  { path: '/', exact: true, component: Landing },
  { path: '/warehouse', component: Warehouse },
  { path: '/admin', component: Admin },
  { path: '/pricing', component: Pricing },
  { path: '/scheduling', component: Scheduling },
  { path: '/dispatching-v1', component: Dispatching },
  { path: '/invoicing', component: Invoicing },
  { path: '/organizations', component: OrganizationsComponent },
  { path: '/safety', component: Safety },
  { path: '/maintenance', component: Maintenance },
  { path: '/audits', component: Audits },
  { path: '/risk', component: Risk },
  { path: '/access-denied', component: AccessDenied },
];

const AppLoader = () => (
  <div
    style={{
      display: 'flex', flex: '1 1 auto', justifyContent: 'center', marginTop: '1em',
    }}
  >
    <LoaderSpinner />
  </div>
);

const LoggedOutApp = () => (
  <Switch>
    <Route exact path='/access-denied' component={AccessDenied} />
  </Switch>
);

const LoggedInApp = () => {
  const {
    isUserLoading, userLoadingErrorMsg, hasAccessTo, userId, userFullName, userEmail, userProperties,
  } = useCurrentUser();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { getCache, clearGridCache } = useDataGridSettingsCache(true);

  const { messagingUrl, messagingUrlV2 } = useSelector(({ messagingUrl }) => messagingUrl);

  const [loadingMessaging, setLoadingMessaging] = useState(true);

  const loadCurrentUser = () => dispatch(getCurrentUser());
  const loadCurrentUserProperties = () => dispatch(getCurrentUserProperties());


  // Main route change check
  // We clear the gridCache here globally
  useEffect(() => {
    const gridCache = getCache();
    if (gridCache
      && location.pathname !== gridCache?.pathname
      && location.pathname !== `${gridCache?.pathname}/${gridCache.selectedId}`) {
      clearGridCache();
    }
  }, [location.pathname]);

  // eslint-disable-line no-shadow
  const redirect = async () => {
    try {
      const shouldLoadsRedirect = await localForage.getItem(SHOULD_LOADS_REDIRECT);
      if (shouldLoadsRedirect && hasAccessTo(VIEW_LOADS_ON_LOGIN)) {
        await localForage.setItem(SHOULD_LOADS_REDIRECT, false);
        // Can't use react-router-dom redirect since this happens before full render.
        // Tap into window.location
        window.location.href = `${window.location.origin}/warehouse/v1/loads`;
        return;
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getMessagingUrl = async () => {
    try {
      const [res1, res2] = await Promise.all([fetchMessagingUrl(), getMessagingUrl2()]);
      setMessagingV2Client({ baseUrl: res2 });
      dispatch(setMessagingUrl({ messagingUrl: res1, messagingUrlV2: res2 }));
    } catch (error) {
      console.error('Failed to get messaging URL', error);
    } finally {
      setLoadingMessaging(false);
    }
  };

  const getDeviceName = () => {
    const parser = new UAParser(window.navigator.userAgent);
    const { device: { vendor, model }, os: { name: osName } } = parser.getResult();

    if (vendor && model) {
      return `${vendor} ${model}`;
    }

    return osName || 'Unknown';
  };

  useEffect(() => {
    // do this in app so we have access to it initially across everything
    loadCurrentUser();
    loadCurrentUserProperties();
    getMessagingUrl();
    const deviceName = getDeviceName();
    trackDevice(deviceName);
  }, []);

  // hit redirect logic when userAccess changes
  useEffect(() => {
    if (!isUserLoading) {
      redirect();
    }
  }, [isUserLoading]);

  if (userLoadingErrorMsg) {
    toastr.error(userLoadingErrorMsg);
  }

  const getToken = useTokenGetter();
  const Messaging = () => (
    <EmployeeMessaging
      apiInstance={authFetch}
      baseURL={messagingUrl}
      baseUrlV2={messagingUrlV2}
      user={{ id: userId, name: userFullName }}
      getToken={getToken}
      getSignalRToken={getSignalRToken}
    />
  );

  // this is used to load a script on the fly from react
  const newScript = (src) =>
    // create a promise for the newScript
    new Promise((resolve, reject) => {
      // create an html script element
      const script = document.createElement('script');
      // set the source of the script element
      script.src = src;
      // set a listener when the script element finishes loading the script
      script.addEventListener('load', () => {
        // resolve if the script element loads
        resolve();
      });
      // set a listener when the script element faces any errors while loading
      script.addEventListener('error', (e) => {
        // reject if the script element has an error while loading the script
        reject(e);
      });
      // append the script element to the body
      document.body.appendChild(script);
    });

  // Aha widget - collects User feedback (TURNED OFF)

  /* const initializeAhaWidget = async ({ userId, userFullName, userEmail }) => {
    // Load Aha script
    // eslint-disable-next-line
    window.ahaLoader = window.ahaLoader || [], ahaLoader.load = (function(w, d, s, o, f, js, fjs) { w['aha-widget'] = o;w[o] = w[o] || function() {(w[o].q = w[o].q || []).push(arguments);}; js = d.createElement(s);fjs = d.getElementsByTagName(s)[0];js.id = o;js.src = f;js.async = 1;fjs.parentNode.insertBefore(js, fjs); });
    ahaLoader.load(window, document, 'script', 'aha', 'https://cdn.aha.io/assets/feedback/feedback.js');

    // Initialize User Aha session for the current URL
    if (window.aha && typeof window.aha === 'function') {
      // Initialize Aha user
      window.aha('initialize', {
        account: 'gulf-winds-international',
        applicationId: '7111792118427559809',
        user: {
          id: userId,
          name: userFullName,
          email: userEmail,
        },
      });
    }
  };

  const destroyAhaWidget = () => {
    // Remove script - otherwize does not allow to re-initialize with a different URL
    const scripts = document.getElementsByTagName('script');
    [...scripts].forEach((script) => {
      if (script.getAttribute('src') === 'https://cdn.aha.io/assets/feedback/feedback.js') {
        script.parentNode.removeChild(script);
      }
    });

    // Destroy widget elements
    if (window.aha && typeof window.aha === 'function') {
      window.aha('destroy');
      delete window.ahaLoader;
      delete window.aha;
    }
  };

  useEffect(() => {
    if (!userId || !userEmail || !userFullName) { return; }
    // Destroy previous page widget
    destroyAhaWidget();
    // Load Aha scripts for the current page
    initializeAhaWidget({ userId, userFullName, userEmail });
  }, [userId, userFullName, userEmail, location.pathname]);
*/

  return (/* The RequestInterceptor adds Bearer token to all axios requests */
    <QueryClientProvider client={queryClient}>
      <UIContextProvider>
        <RequestInterceptor>
          <OrderSupportRequestsConnectionProvider>
            {pathname === '/messaging' ? (
              (isUserLoading || loadingMessaging) ? (
                <AppLoader />
              ) : (
                <Switch>
                  <AccessRoute
                    exact
                    key='messaging'
                    path='/messaging'
                    component={Messaging}
                    requiredAccess={[CAN_VIEW_CMS]}
                  />
                </Switch>
              )) : (
                <AppLayout>
                {isUserLoading ? (
                    <AppLoader />
                ) : (
                  <Suspense fallback={<AppLoader />}>
                    <Switch>
                      {routes.map(({ exact, path, component }) => (
                        <AccessRoute
                          exact={exact}
                          key={path}
                          path={path}
                          component={component}
                          requiredAccess={[CAN_VIEW_CMS]}
                        />
                      ))}
                    </Switch>
                  </Suspense>
                )}
              </AppLayout>
            )}
            <ToastContainer />
            <ModalContainer />
            {/* <UpdateAlert /> */}
            <ReactTooltip className='gwi-tooltip' multiline={false} />
            {/* //HACK: Use ModalContainer instead of ConfirmDialogRoot when modal stacking is available */}
            <ConfirmDialogRoot />
          </OrderSupportRequestsConnectionProvider>
        </RequestInterceptor>
      </UIContextProvider>
    </QueryClientProvider>
  );
};

const App = () => {
  useAppMsalAuthentication();

  const dispatch = useDispatch();

  // Set initial theme
  useEffect(() => {
    const setInitialTheme = () => {
      if (window?.localStorage) {
        const needSwitch = window.localStorage.getItem('theme_mode') === 'dark';
        if (needSwitch) {
          switchTheme('dark', dispatch);
        }
      }
    };

    setInitialTheme();
  }, []);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Router>
        <AuthenticatedTemplate>
          <LoggedInApp />
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <LoggedOutApp />
        </UnauthenticatedTemplate>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
